/**
 * useSecretSequence
 *
 * Listens for a typed sequence of keys and fires `onMatch` when the user
 * has typed `targetWord` (case-insensitive) on a 6-character (or word-length)
 * ring buffer.
 *
 * Signature:
 *   useSecretSequence(targetWord: string, onMatch: () => void): void
 *
 * Behavior:
 *   - Maintains a ring buffer of the last `targetWord.length` keys pressed.
 *   - On every keydown, the buffer is updated and compared (case-insensitive)
 *     to `targetWord`. On match, `onMatch()` fires once and the buffer resets.
 *   - After 2 seconds of idle (no keystroke), the buffer is cleared.
 *   - Case-insensitive: "SECRET", "Secret", "secret" all match `targetWord = 'secret'`.
 *
 * Safety guards (security threat model — see Phase 31 threat register):
 *   - T-31-01: Ring buffer lives in `useRef` only. Never persisted to
 *              local-storage, session-storage, cookies, network requests,
 *              or developer logs. Cleared on idle timeout AND on unmount.
 *   - T-31-02: Ignores keystrokes when `document.activeElement` matches
 *              `input, textarea, select, [contenteditable]`. Form-input
 *              keystrokes are NEVER captured.
 *   - T-31-03: Ignores keystrokes when Ctrl, Alt, or Meta modifiers are held.
 *              Browser shortcuts (e.g., Cmd+R refresh) cannot collide with
 *              the secret sequence. Modifier-only keypresses are also ignored.
 *
 * Caller responsibility (NOT this hook's job):
 *   - Suppression on /contact, /pm/*, /casino/*, /egyptian-ratscrew, /gin-rummy,
 *     /speed: the CALLER (Phase 35's homepage component) decides when to mount
 *     this hook. The hook does not read useLocation — it stays decoupled from
 *     React Router so it remains reusable.
 *
 * StrictMode safety:
 *   - `handleKeyDown` is a NAMED const inside the useEffect body so the same
 *     reference is registered and removed. Inline arrows would leak listeners
 *     under StrictMode's double-mount.
 *   - The idle timeout is cleared in cleanup so unmounted components don't
 *     leave stale timers.
 *
 * Stale-closure safety:
 *   - `targetWord` and `onMatch` are read through refs (`targetRef`, `onMatchRef`)
 *     so the inner handler always sees the latest values without re-attaching
 *     the global listener on every render.
 */

import { useEffect, useRef } from 'react';

export const useSecretSequence = (
  targetWord: string,
  onMatch: () => void
): void => {
  const bufferRef = useRef<string[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const targetRef = useRef<string>(targetWord);
  const onMatchRef = useRef<() => void>(onMatch);

  // Keep the refs in sync with the latest props each render — no re-attach.
  useEffect(() => {
    targetRef.current = targetWord;
    onMatchRef.current = onMatch;
  }, [targetWord, onMatch]);

  useEffect(() => {
    const IGNORED_ELEMENTS = 'input, textarea, select, [contenteditable]';
    const MODIFIER_KEYS = new Set<string>(['Control', 'Alt', 'Meta', 'Shift']);
    const IDLE_RESET_MS = 2000;

    const handleKeyDown = (event: KeyboardEvent): void => {
      // T-31-02: focus is inside a form element — bail out
      const active = document.activeElement;
      if (active && active.matches && active.matches(IGNORED_ELEMENTS)) {
        return;
      }

      // T-31-03: Ctrl/Alt/Meta combo or modifier-only keypress — bail out
      if (event.ctrlKey || event.altKey || event.metaKey) return;
      if (MODIFIER_KEYS.has(event.key)) return;

      const key = event.key.toLowerCase();
      // Only single printable characters feed the buffer
      if (key.length !== 1) return;

      const target = targetRef.current.toLowerCase();
      bufferRef.current = [...bufferRef.current, key].slice(-target.length);
      if (target === 'cnf') console.log('[seq:cnf] key:', key, 'buffer:', bufferRef.current.join(''));

      // Reset / reschedule the idle clear
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        bufferRef.current = [];
      }, IDLE_RESET_MS);

      // Match check
      if (bufferRef.current.join('') === target) {
        if (target === 'cnf') console.log('[seq:cnf] MATCHED — calling onMatch');
        bufferRef.current = [];
        onMatchRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // T-31-01: scrub the buffer on unmount — never leave stale keys in memory
      bufferRef.current = [];
    };
  }, []); // empty deps — handler reads from refs, listener attached once per mount
};
